/**
 * Base de recettes complète — alpha, item 9.
 *
 * ~150 recettes FR **curées** (classiques européens + meilleure
 * complétude — cf. `scripts/curate-recipes.mjs`, feedback alpha item 6)
 * vivent dans `public/recipes-db.json` (~215 Ko brut). Le
 * fichier n'est **jamais bundlé** : il est fetché **à la demande** au
 * premier usage du picker de recettes, puis caché (mémoire + cache
 * runtime du Service Worker). En cas d'échec réseau, le picker retombe
 * sur les 20 recettes du proto (`RECIPE_DB`) — mode dégradé §2.4.
 *
 * La recherche est insensible à la casse et aux accents, et matche le
 * nom, la catégorie et les ingrédients (nom en premier dans le tri).
 */
import type { Recipe } from './recipes.js';

/** Recette complète telle que servie par `recipes-db.json`. */
export interface FullRecipe {
  id: number;
  name_fr: string;
  category: string;
  cuisine: string;
  /** Temps de préparation (minutes). */
  prep_time: number;
  servings: number;
  difficulty: 'easy' | 'medium' | 'hard' | string;
  health_tags: string[];
  ingredients_flat: string[];
  steps: string[];
  nutrition: {
    calories: number;
    proteins: number;
    carbs: number;
    fat: number;
    fiber: number;
  };
}

// ── Chargement lazy + cache ───────────────────────────────────────────

let cache: FullRecipe[] | null = null;
let inFlight: Promise<FullRecipe[]> | null = null;
// Index de recherche précalculé (T1) : normaliser 8 872 noms à chaque
// frappe coûtait ~10× trop cher — on ne normalise qu'une fois au
// chargement. WeakMap-free : reconstruit avec le cache.
let searchIndex: { r: FullRecipe; name: string; rest: string }[] | null = null;

function buildIndex(data: FullRecipe[]): void {
  searchIndex = data.map((r) => ({
    r,
    name: normalize(r.name_fr),
    rest: normalize(r.category + ' ' + r.ingredients_flat.join(' ')),
  }));
}

/** Base déjà chargée ? (rendu synchrone du picker). */
export function getRecipesDbNow(): FullRecipe[] | null {
  return cache;
}

/**
 * Charge la base au premier appel (déduplique les appels concurrents).
 * Erreur réseau → `[]` (le picker garde le fallback proto) sans throw.
 */
export async function loadRecipesDb(fetchImpl: typeof fetch = fetch): Promise<FullRecipe[]> {
  if (cache) return cache;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const res = await fetchImpl('/recipes-db.json');
      if (!res.ok) throw new Error(`HTTP ${res.status.toString()}`);
      const data = (await res.json()) as FullRecipe[];
      cache = data;
      buildIndex(data);
      return data;
    } catch {
      // Mode dégradé : pas de cache → un prochain appel retentera.
      return [];
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** @internal pour tests. */
export function _resetRecipesDbForTests(): void {
  cache = null;
  inFlight = null;
  searchIndex = null;
}

// ── Recherche ─────────────────────────────────────────────────────────

/** Normalise pour la recherche : minuscules + accents retirés. */
export function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Recherche dans la base. Tri : match du nom en premier (préfixe puis
 * inclusion), puis catégorie/ingrédients. `limit` borne le rendu (le
 * picker n'affiche pas 8 000 lignes).
 */
export function searchRecipes(all: readonly FullRecipe[], query: string, limit = 50): FullRecipe[] {
  const q = normalize(query.trim());
  if (q.length < 2) return [];
  // Sert l'index précalculé quand il correspond au cache ; sinon
  // (tableau custom passé par les tests) normalise à la volée.
  const entries =
    searchIndex && all === cache
      ? searchIndex
      : all.map((r) => ({
          r,
          name: normalize(r.name_fr),
          rest: normalize(r.category + ' ' + r.ingredients_flat.join(' ')),
        }));
  const starts: FullRecipe[] = [];
  const inName: FullRecipe[] = [];
  const inRest: FullRecipe[] = [];
  for (const e of entries) {
    if (e.name.startsWith(q)) starts.push(e.r);
    else if (e.name.includes(q)) inName.push(e.r);
    else if (e.rest.includes(q)) inRest.push(e.r);
    if (starts.length >= limit) break;
  }
  return [...starts, ...inName, ...inRest].slice(0, limit);
}

// ── Adaptation vers le type Recipe du proto ───────────────────────────

const DIFF_LABEL: Record<string, Recipe['diff']> = {
  easy: 'Facile',
  medium: 'Moyen',
  hard: 'Difficile',
};

const CATEGORY_ICON: Record<string, string> = {
  Volaille: '🍗',
  Poisson: '🐟',
  'Fruits de mer': '🦐',
  Desserts: '🍰',
  Légumes: '🥦',
  Féculents: '🍚',
  Boeuf: '🥩',
  Veau: '🥩',
  Porc: '🥩',
  'Mouton agneau': '🥩',
  Oeufs: '🥚',
};

/** Icône emoji par catégorie (la base n'en fournit pas). */
export function iconForCategory(category: string): string {
  return CATEGORY_ICON[category] ?? '🍽️';
}

/**
 * Adapte une recette complète vers le type `Recipe` du proto — c'est
 * ce format que `applyRecipeToSlot` stocke dans le repas (nom, tag,
 * portions, ingrédients pour la liste de courses).
 */
export function toRecipe(full: FullRecipe): Recipe {
  return {
    id: full.id,
    name: full.name_fr,
    icon: iconForCategory(full.category),
    tag: full.category,
    cuisine: full.cuisine,
    time: full.prep_time,
    bs: full.servings,
    diff: DIFF_LABEL[full.difficulty] ?? 'Facile',
    ing: full.ingredients_flat.map((n) => ({ n, q: 0, u: '' })),
    steps: full.steps,
  };
}
